/**
 * @author pjh
 * @date 2025.09.02
 * @description 폰트 목록 패널 컴포넌트
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CssFontDetail, FontFile } from '../../../type/cssTemplate';
import FontPreview from './FontPreview';
import { useProductStore } from '../../../store/useProduct'

interface IFontListPanel {
    selectedFontDetail: CssFontDetail | null;
    selectedFontsSize: number;
    currentFontIndex: number;
    role: string;
    isUploading: boolean;
    onNavigateFont: (direction: 'prev' | 'next') => void;
    onFontUseChange: (fontFile: FontFile, index: number) => void;
    onBoldUpload: (e: React.ChangeEvent<HTMLInputElement>, fontFile: FontFile) => void;
    onAddFiles?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onUploadToNewFonts?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    productId: string;
    uploadInputRef?: React.RefObject<HTMLInputElement>;
    scrollPositionRef: React.MutableRefObject<{ x: number; y: number }>;
    onCssNameChange: (newCssName: string) => void;
}

const FontListPanel: React.FC<IFontListPanel> = ({
    selectedFontDetail,
    selectedFontsSize,
    currentFontIndex,
    role,
    isUploading,
    onNavigateFont,
    onFontUseChange,
    onBoldUpload,
    onAddFiles,
    onUploadToNewFonts,
    productId,
    scrollPositionRef,
    onCssNameChange
}) => {
    // iframe 스크롤 위치 저장용
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const lastPreviewRequestKeyRef = useRef<string | null>(null);
    const { getProductPreview, getProductDetail, productDetail } = useProductStore();

    // 상품 정보 로드
    useEffect(() => {
        if (productId) {
            getProductDetail(productId);
        }
    }, [productId, getProductDetail]);

    const productThumbnailId = productDetail?.thumbnailFile?.id;
    const productInlineId = productDetail?.inlineFile?.id;
    const productEndId = productDetail?.endFile?.id;

    useEffect(() => {
        if (!productId || !productThumbnailId || !productInlineId) {
            return;
        }

        let fontMapper = null;
        if (selectedFontDetail?.originFile) {
            const activeFont = selectedFontDetail.originFile.find((font) => font.use);
            if (activeFont) {
                fontMapper = {
                    cssName: selectedFontDetail.cssName,
                    fileId: activeFont.id || activeFont.cssFontId || '',
                    boldFileId: activeFont.boldFontFile?.id || null,
                };
            }
        }

        // cssName은 fontMapper(활성 폰트)가 있을 때만 미리보기에 영향을 줌.
        // 활성 폰트 없을 때 cssName을 key에 포함하면, 신규 기준에서 cssName 타이핑 시마다
        // useEffect가 재실행되며 미리보기 fetch가 발생 → iframe 깜빡임 + 입력 lag.
        const requestKey = JSON.stringify({
            productId,
            thumbnailId: productThumbnailId,
            inlineId: productInlineId,
            endId: productEndId || null,
            fontId: fontMapper?.fileId || null,
            boldFileId: fontMapper?.boldFileId || null,
            cssName: fontMapper ? fontMapper.cssName : null,
        });

        if (lastPreviewRequestKeyRef.current === requestKey) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const preview = await getProductPreview(
                    productId,
                    productThumbnailId,
                    productInlineId,
                    productEndId,
                    fontMapper ? { fontMapper: fontMapper } : undefined
                );

                if (!cancelled) {
                    setPreviewHtml(preview);
                    lastPreviewRequestKeyRef.current = requestKey;
                }
            } catch {
                if (!cancelled) {
                    lastPreviewRequestKeyRef.current = null;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        productId,
        productThumbnailId,
        productInlineId,
        productEndId,
        selectedFontDetail,
        getProductPreview,
    ]);

    useEffect(() => {
        lastPreviewRequestKeyRef.current = null;
        setPreviewHtml('');
    }, [productId]);

    // selectedFontDetail이 없으면 렌더링하지 않음
    if (!selectedFontDetail) return null;

    return (
        <div style={{
            flex: 1,
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            padding: '20px',
            border: '1px solid #e5e7eb',
            maxHeight: '700px',
            overflowY: 'auto'
        }}>
            {/* 헤더 */}
            <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>{selectedFontDetail.remark}</span>에서 사용가능한 폰트 목록
                        {selectedFontsSize > 1 && (
                            <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: '400',
                                marginTop: '4px'
                            }}>
                                ({currentFontIndex + 1} / {selectedFontsSize})
                            </div>
                        )}
                    </div>
                    {role === 'ROLE_ADMIN' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', whiteSpace: 'nowrap' }}>변경 기준:</label>
                            <input
                                type="text"
                                value={selectedFontDetail.cssName}
                                onChange={(e) => onCssNameChange(e.target.value)}
                                style={{
                                    padding: '6px 10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    fontSize: '12px',
                                    minWidth: '300px'
                                }}
                            />
                        </div>
                    )}
                </div>
                {selectedFontsSize > 1 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={() => onNavigateFont('prev')}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            &lt;
                        </button>
                        <button
                            onClick={() => onNavigateFont('next')}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            &gt;
                        </button>
                    </div>
                )}
            </div>

            {/* 폰트 목록과 미리보기를 좌우로 배치 */}
            <div style={{
                display: 'flex',
                gap: '16px'
            }}>
                {/* 왼쪽: 사용 가능한 폰트 목록 */}
                <div style={{ flex: '1 1 60%' }}>
                    <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '10px'
                    }}>
                        사용 가능한 폰트 ({selectedFontDetail.originFile?.length || 0}개):
                    </div>
                    {selectedFontDetail.originFile && selectedFontDetail.originFile.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            {selectedFontDetail.originFile.map((fontFile, index) => (
                                <FontFileItem
                                    key={index}
                                    fontFile={fontFile}
                                    index={index}
                                    cssName={selectedFontDetail.cssName}
                                    onClick={() => onFontUseChange(fontFile, index)}
                                    onBoldUpload={(e) => onBoldUpload(e, fontFile)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#9ca3af',
                            fontSize: '12px',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                        }}>
                            사용 가능한 폰트가 없습니다
                        </div>
                    )}
                </div>
                
                {/* 오른쪽: 폰트 사용 위치 미리보기 */}
                <div style={{ flex: '0 0 40%' }}>
                    <h3 style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '10px'
                    }}>폰트 사용 위치<span className="!text-blue-600">(파란 테두리)</span></h3>
                    
                    {/* 미리보기 영역 */}
                    <div style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        overflow: 'hidden',
                        width: '430px',
                        height: '550px'
                    }}>
                        <iframe
                            ref={iframeRef}
                            onLoad={() => {
                                const iframe = iframeRef.current;
                                if (iframe && iframe.contentWindow) {
                                    const iframeWindow = iframe.contentWindow;
                                    // 스크롤 위치 저장
                                    iframeWindow.scrollTo(scrollPositionRef.current.x, scrollPositionRef.current.y);

                                    iframeWindow.onscroll = () => {
                                        scrollPositionRef.current = {
                                            x: iframeWindow.scrollX,
                                            y: iframeWindow.scrollY
                                        };
                                    };
                                }
                            }}
                            style={{
                                width: '210mm', // A4 폭
                                height: '297mm', // A4 높이
                                transform: 'scale(0.530)',
                                transformOrigin: 'top left',
                                border: 'none',
                                display: 'block'
                            }}
                            srcDoc={previewHtml}
                            title="상품 미리보기"
                        />
                    </div>
                </div>
            </div>

            {/* 관리자용 폰트 추가 섹션 */}
            {role === 'ROLE_ADMIN' && selectedFontDetail.id && onAddFiles && (
                <AdminFontUploadSection
                    selectedFontsSize={selectedFontsSize}
                    isUploading={isUploading}
                    onChange={onAddFiles}
                    isNewFont={false}
                />
            )}
            
            {/* 신규 폰트용 일괄 업로드 섹션 */}
            {role === 'ROLE_ADMIN' && !selectedFontDetail.id && onAddFiles && (
                <AdminFontUploadSection
                    selectedFontsSize={selectedFontsSize}
                    isUploading={isUploading}
                    onChange={onAddFiles}
                    isNewFont={true}
                />
            )}
        </div>
    );
};

// 폰트 파일 아이템 컴포넌트
const FontFileItem: React.FC<{
    fontFile: FontFile;
    index: number;
    cssName: string;
    onClick: () => void;
    onBoldUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ fontFile, index, cssName, onClick, onBoldUpload }) => {
    const fontName = `${cssName}_origin_${index}`;
    const boldInputId = `bold-upload-${fontFile.id}`;
    
    return (
        <div
            style={{
                width: '180px',
                backgroundColor: fontFile.use ? '#dbeafe' : 'white',
                border: fontFile.use ? '2px solid #3b82f6' : '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
            }}
            onClick={onClick}
        >
            {/* 기본(얇은) 폰트 정보 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: fontFile.use ? '#1e40af' : '#1f2937' }}>
                    {fontFile.originalFileName || `폰트 ${index + 1}`}
                </div>
                {fontFile.use && (
                    <span style={{ fontSize: '10px', backgroundColor: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                        사용 중
                    </span>
                )}
            </div>
            <div style={{ marginTop: '4px', padding: '4px', backgroundColor: fontFile.use ? 'white' : '#f9fafb', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {fontFile.boldFontFile ? (
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #eee' }}>
                            <FontPreview fontName={fontName} fontSize={14} style={{ margin: 0 }} />
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <FontPreview fontName={`${fontName}_bold`} fontSize={14} style={{ margin: 0, fontWeight: 'bold' }} />
                        </div>
                    </div>
                ) : (
                    <FontPreview fontName={fontName} fontSize={14} style={{ margin: 0 }} />
                )}
            </div>

            {/* 굵은 폰트 정보 및 업로드 */}
            <div style={{ marginTop: '10px', paddingTop: '2px' }} onClick={(e) => e.stopPropagation()}>
                {fontFile.boldFontFile == null ? (
                    <>
                        <label htmlFor={boldInputId} style={{
                            fontSize: '11px',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}>
                            + 굵은 폰트 추가
                        </label>
                        <input type="file" id={boldInputId} style={{display: 'none'}} onChange={onBoldUpload}
                                       accept=".otf, .ttf, .woff, .woff2, .eot, .svg"/>
                    </>
                ) : null}
            </div>
        </div>
    );
};

// 관리자용 업로드 섹션
const AdminFontUploadSection: React.FC<{
    selectedFontsSize: number;
    isUploading: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isNewFont: boolean;
}> = ({ selectedFontsSize, isUploading, onChange, isNewFont }) => {
    return (
        <div style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '2px solid #e5e7eb'
        }}>
            <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                <span>{isNewFont ? '📁' : '➕'}</span>
                <span>
                    {isNewFont 
                        ? '신규 폰트 파일 업로드'
                        : selectedFontsSize > 1
                            ? `선택된 ${selectedFontsSize}개 폰트에 일괄 추가`
                            : '사용 가능한 폰트 추가'
                    }
                </span>
            </div>
            <input
                key={`font-upload-${isNewFont ? 'new' : 'existing'}`}
                type="file"
                accept=".otf, .ttf, .woff, .woff2, .eot, .svg"
                onChange={onChange}
                multiple
                disabled={isUploading}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.6 : 1
                }}
            />
            <div style={{
                fontSize: '11px',
                color: '#6b7280',
                marginTop: '6px'
            }}>
                {isNewFont
                    ? '* 선택된 신규 폰트에 동일한 파일이 일괄 적용됩니다.'
                    : selectedFontsSize > 1
                        ? `* 선택된 ${selectedFontsSize}개 폰트에 동일한 폰트 파일이 일괄 추가됩니다.`
                        : '* 여러 폰트 파일을 선택하여 한 번에 추가할 수 있습니다.'
                }
            </div>
        </div>
    );
};


export default FontListPanel;
